// FILE: RecentCasesCard.js
import React, { useEffect, useState } from "react";
import { Box, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { useTranslation } from 'react-i18next';
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import { useDispatch, useSelector } from "react-redux";
import { deleteAppointment, listAppointments } from "../redux/actions/appointmentActions";
import {
  mediumBold,
  smallBold,
  smallTitle,
  thinBold,
  typoBase,
  typoThin,
} from "../styles/typography";
import { useTheme } from "@mui/material/styles";
import { formatDate as formatDateUtil } from "../utils/dateUtils";

const AgendaHomeCard = ({ appointments }) => {
  const { t } = useTranslation(['common', 'buttons']);
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [hoveredAppointmentId, setHoveredAppointmentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  useEffect(() => {
    console.log('AgendaHomeCard - appointments:', appointments);
    console.log('AgendaHomeCard - appointments length:', appointments?.length);
    console.log('AgendaHomeCard - is array:', Array.isArray(appointments));
  }, [appointments]);
  
  const handleNavigate = (id) => {
    navigate(`/appointment/${id}`);
  };
  
  const handleRelationshipNavigate = (relationshipId) => {
    navigate(`/relationships/${relationshipId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDateUtil(dateString);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDeleteClick = (appointmentId, e) => {
    e.stopPropagation();
    setAppointmentToDelete(appointmentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (appointmentToDelete) {
      try {
        await dispatch(deleteAppointment(appointmentToDelete));
        // Refresh appointments list to ensure UI is updated
        dispatch(listAppointments());
      } catch (error) {
        console.error('Error deleting appointment:', error);
        alert(error?.message || 'Failed to delete appointment. Please try again.');
      }
    }
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
  };

  return (
    <Box
      className="list-items"
      sx={{
        marginTop: 2,
        overflowY: "auto",
        maxHeight: "450px",
        "&::-webkit-scrollbar": {
          width: "5px", // Set the width of the scrollbar
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "lightgrey", // Set the color of the scrollbar thumb
          borderRadius: "0", // Set the border radius of the scrollbar thumb
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#888", // Set the color of the scrollbar thumb on hover
        },
      }}
    >
      {appointments && Array.isArray(appointments) && appointments.length > 0 ? (
        appointments.map((appointment) => (
          <Box
            key={appointment._id}
            onMouseEnter={() => setHoveredAppointmentId(appointment._id)}
            onMouseLeave={() => setHoveredAppointmentId(null)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "top",
              borderBottom: "1px solid lightgrey",
              padding: "5px",
              paddingTop: "5px",
              "&:hover": {
                backgroundColor: "#ecf5fb",
              },
            }}
          >
          <Box
            //
            sx={{
              display: "flex",
              alignItems: "top",
              justifyContent: "top",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            <Box
              sx={{
                color: "rgb(53, 53, 53)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "11px",
                height: "15.75px",
                lineHeight: "21px",
                gap: "5px",
                flexWrap: "nowrap",
              }}
            >
              <Box
                sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "27ch",
              }}
            >
              {formatDate(appointment?.startDate)}
              {", "}
              {formatTime(appointment?.startDate)}{" "}
              -{" "}
              {formatDate(appointment?.endDate) ===
              formatDate(appointment?.startDate)
                ? formatTime(appointment?.endDate)
                : `${formatDate(appointment?.endDate)} ${formatTime(appointment?.endDate)}`}
              </Box>
              {hoveredAppointmentId === appointment._id && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteClick(appointment._id, e)}
                  sx={{
                    padding: "2px",
                    marginLeft: "5px",
                    color: "rgb(229, 32, 46)",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "rgba(229, 32, 46, 0.1)",
                    },
                  }}
                >
                  <DeleteOutline sx={{ fontSize: "16px" }} />
                </IconButton>
              )}
            </Box>
            <Box
              component="p"
              sx={{
                color: "rgb(0, 91, 146)",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "11.5px",
                height: "15.75px",
                lineHeight: "15.75px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "25ch",
                marginTop: "0",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={() =>
                navigate(`/files/${appointment?.case?._id}/overview`)
              }
            >
              {appointment?.case
                ? `${appointment.case.fileNo} ${appointment.case.name}`
                : "No Case Info"}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              textAlign: "right",
              margin: 0,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Box
                sx={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: appointment.colour,
                  marginRight: "5px",
                }}
              />
              <Box
                sx={{
                  color: "rgb(0, 91, 146)",
                  cursor: "pointer",
                  display: "block",
                  fontFamily:
                    '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                  fontSize: "11.5px",
                  fontWeight: 400,
                  height: "13.5px",
                  lineHeight: "13.5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "23ch",
                  paddingBottom: "2px",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={() => handleNavigate(appointment._id)}
              >
                {appointment?.subject}
              </Box>
            </Box>

            <Box
              sx={{
                color: "rgb(53, 53, 53)",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "11.5px",
                fontWeight: 400,
                height: "15.75px",
                lineHeight: "15.75px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Box
                component="span"
                onClick={() => handleRelationshipNavigate(appointment?.owner?._id)}
                sx={{
                  cursor: "pointer",
                  color: "rgb(0, 91, 146)",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {appointment?.owner?.firstName +
                  " " +
                  appointment?.owner?.lastName}
              </Box>
              <Avatar
                alt={user?.firstName}
                src={
                  user?.avatar ? user?.avatar : "/static/images/avatar/1.jpg"
                }
                sx={{
                  fontFamily:
                    '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                  fontSize: "9px",
                  height: "18px",
                  lineHeight: "18px",
                  width: "18px", // Ensure the width matches the height for a square avatar
                  marginLeft: "8px",
                }}
              />
            </Box>
            <Box
              sx={{
                color: "rgb(53, 53, 53)",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.256px",
                fontWeight: 500,
                height: "12.375px",
                lineHeight: "12.384px",
                marginTop: "2px",
              }}
            >
              {appointment?.type}
            </Box>
          </Box>
        </Box>
        
        ))
      ) : (
        <Box sx={{ padding: "32px 24px", textAlign: "center" }}>
          {console.log('AgendaHomeCard - showing empty state')}
          {t('no_data_available')}
        </Box>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('common:confirm_delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('common:confirm_delete_appointment')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:secondary.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            {t('buttons:danger.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
  );
};

export default AgendaHomeCard;
